import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Axe, Pickaxe, Fish, Leaf, X } from "lucide-react";
import { SFX } from "@/lib/sounds";

const SKILL_ICON = {
  woodcutting: Axe,
  mining: Pickaxe,
  fishing: Fish,
  gathering: Leaf,
};

const SKILL_LABEL = {
  woodcutting: "Woodcutting",
  mining: "Mining",
  fishing: "Fishing",
  gathering: "Gathering",
};

const TIER_TINT = ["#8c8c8c", "#B0ADA5", "#4A7BC7", "#B89C3A", "#8A0303", "#E33333"];

/**
 * Tap-timing minigame. An indicator sweeps a bar; player must tap "STRIKE"
 * while it overlaps the shaded hit-zone. Higher-tier resources have smaller zones.
 * Called via TravelPanel when the travel outcome kind === "gather_offer".
 *
 * Props:
 *  - mg: { session_id, skill, tier, resource, zone, value, xp, text }
 *  - onClose: () => void
 *  - onResolved: (result) => void   // result: { character, success, resource, tier, count?, xp }
 */
export default function GatherMinigame({ mg, onClose, onResolved }) {
  const [phase, setPhase] = useState("prompt"); // prompt | play | done
  const [progress, setProgress] = useState(0); // 0..1 indicator position
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const dirRef = useRef(1);
  const finalPosRef = useRef(0);

  // Zone shrinks with tier — server already picks; we place it slightly randomly.
  const zoneStart = useRef(0.35 + Math.random() * (0.5 - mg.zone));
  const zoneEnd = zoneStart.current + mg.zone;
  const periodMs = Math.max(900, 1700 - mg.tier * 150); // higher tier = faster indicator

  useEffect(() => {
    if (phase !== "play") return;
    let mounted = true;
    startRef.current = performance.now();
    const loop = (now) => {
      if (!mounted) return;
      const t = (now - startRef.current) % (periodMs * 2);
      const p = t < periodMs ? t / periodMs : 1 - (t - periodMs) / periodMs;
      finalPosRef.current = p;
      setProgress(p);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [phase, periodMs]);

  const strike = async () => {
    if (busy || phase !== "play") return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const pos = finalPosRef.current;
    const hit = pos >= zoneStart.current && pos <= zoneEnd;
    setBusy(true);
    try {
      const r = await api.minigameResolve(mg.session_id, hit);
      if (hit) SFX.loot(); else SFX.denied();
      setResult(r);
      setPhase("done");
      onResolved?.(r);
    } catch (e) {
      SFX.denied();
      toast.error(e?.response?.data?.detail || "The moment passes.");
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Explicit "walk away" = resolve with hit=false so server clears the pending game.
      const r = await api.minigameResolve(mg.session_id, false);
      onResolved?.(r);
    } catch (e) { /* silent */ }
    finally {
      setBusy(false);
      onClose();
    }
  };

  const Icon = SKILL_ICON[mg.skill] || Leaf;
  const tint = TIER_TINT[Math.min(TIER_TINT.length - 1, mg.tier)];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="gather-minigame"
    >
      <div className="w-full max-w-sm panel p-4 space-y-3 relative">
        <button
          data-testid="minigame-close"
          onClick={decline}
          className="absolute top-2 right-2 text-[#666] hover:text-[#E2DFD8]"
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color: tint }} strokeWidth={1.5} />
          <span className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
            {SKILL_LABEL[mg.skill] || mg.skill}
          </span>
          <span
            className="ml-auto font-mono text-[10px] px-2 py-0.5 border tracking-widest uppercase"
            style={{ color: tint, borderColor: tint }}
            data-testid="minigame-tier"
          >
            Tier {mg.tier}
          </span>
        </div>

        <div className="font-body text-sm text-[#E2DFD8] italic leading-relaxed">
          {mg.text}
        </div>

        <div className="panel-inner p-2 font-mono text-[10px] text-[#8c8c8c] uppercase tracking-widest">
          Prize: <span className="text-[#E2DFD8]">{mg.resource}</span> · {mg.value}g · +{mg.xp} xp
        </div>

        {phase === "prompt" && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              data-testid="minigame-decline"
              onClick={decline}
              disabled={busy}
              className="py-2 border border-[#262626] text-[#8c8c8c] font-heading text-xs tracking-widest uppercase"
            >
              Walk On
            </button>
            <button
              data-testid="minigame-start"
              onClick={() => { SFX.step(); setPhase("play"); }}
              disabled={busy}
              className="py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase"
            >
              Attempt
            </button>
          </div>
        )}

        {phase === "play" && (
          <div className="space-y-3 pt-1" data-testid="minigame-play">
            <div
              className="relative h-8 border border-[#262626] bg-[#0a0a0a] overflow-hidden select-none"
              onClick={strike}
              data-testid="minigame-bar"
            >
              {/* Zone */}
              <div
                className="absolute top-0 bottom-0 opacity-70"
                style={{
                  left: `${zoneStart.current * 100}%`,
                  width: `${(zoneEnd - zoneStart.current) * 100}%`,
                  background: `linear-gradient(180deg, ${tint}55, ${tint}22)`,
                  borderLeft: `1px solid ${tint}`,
                  borderRight: `1px solid ${tint}`,
                }}
              />
              {/* Center guide */}
              <div
                className="absolute top-0 bottom-0 border-l border-dashed opacity-40"
                style={{ left: `${(zoneStart.current + (zoneEnd - zoneStart.current) / 2) * 100}%`, borderColor: tint }}
              />
              {/* Indicator */}
              <div
                className="absolute top-0 bottom-0 w-0.5"
                style={{
                  left: `${progress * 100}%`,
                  background: "#E2DFD8",
                  boxShadow: "0 0 6px #E2DFD8",
                }}
              />
            </div>
            <button
              data-testid="minigame-strike"
              onClick={strike}
              disabled={busy}
              className="w-full py-3 border border-[#E55A00] text-[#E55A00] font-heading text-sm tracking-[0.3em] uppercase hover:bg-[#E55A00]/10"
            >
              Strike
            </button>
            <div className="font-mono text-[9px] text-[#666] uppercase tracking-widest text-center">
              Tap when the mark crosses the shaded zone
            </div>
          </div>
        )}

        {phase === "done" && result && (
          <div className="pt-1 space-y-2" data-testid="minigame-done">
            {result.success ? (
              <div className="panel-inner p-3 text-center space-y-1">
                <div className="font-heading text-sm tracking-widest uppercase" style={{ color: tint }}>
                  Struck true
                </div>
                <div className="font-body text-sm text-[#E2DFD8]">
                  +{result.count}× {result.resource}
                </div>
                <div className="font-mono text-[10px] text-[#8c8c8c]">+{result.xp} {mg.skill} xp</div>
              </div>
            ) : (
              <div className="panel-inner p-3 text-center space-y-1">
                <div className="font-heading text-sm tracking-widest uppercase text-[#8A0303]">
                  Missed
                </div>
                <div className="font-body text-xs text-[#8c8c8c] italic">
                  The {mg.resource} slips away into the fog.
                </div>
              </div>
            )}
            <button
              data-testid="minigame-continue"
              onClick={onClose}
              className="w-full py-2 border border-[#262626] text-[#B0ADA5] font-heading text-xs tracking-widest uppercase"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
